/**
 * Chatbot utilities
 */

/**
 * Mask a sensitive string for safe console logging.
 * Shows length plus a short prefix/suffix; never logs the full value.
 * @param {string|null|undefined} value
 * @param {object} [options]
 * @param {number} [options.visibleStart=4]
 * @param {number} [options.visibleEnd=4]
 * @returns {string}
 */
export const maskSensitiveValue = (value, options = {}) => {
  const { visibleStart = 4, visibleEnd = 4 } = options;

  if (value == null || value === '') {
    return '(empty)';
  }

  const str = String(value);
  const length = str.length;

  if (length <= visibleStart + visibleEnd) {
    return `${'*'.repeat(Math.max(length, 4))} (len=${length})`;
  }

  const start = str.slice(0, visibleStart);
  const end = str.slice(-visibleEnd);
  return `${start}${'*'.repeat(Math.min(length - visibleStart - visibleEnd, 24))}${end} (len=${length})`;
};

/**
 * HTTP Authorization / Bearer values must be printable ASCII without whitespace or control chars.
 * Binary/garbled decrypt output fails setRequestHeader with an invalid Bearer value.
 * @param {string} value
 * @returns {boolean}
 */
export const isValidHttpHeaderValue = (value) => {
  if (!value || typeof value !== 'string') return false;
  // Printable ASCII excluding space (0x21–0x7E) — Bearer tokens must not contain whitespace
  return /^[\x21-\x7E]+$/.test(value);
};

/**
 * Summarize non-printable / suspicious characters in a decrypted token (for debug only).
 * @param {string} value
 * @returns {{ printableRatio: string, nonPrintableCount: number, hasWhitespace: boolean, sampleCodes: string }}
 */
export const inspectTokenCharset = (value) => {
  if (!value || typeof value !== 'string') {
    return {
      printableRatio: '0%',
      nonPrintableCount: 0,
      hasWhitespace: false,
      sampleCodes: ''
    };
  }

  let nonPrintableCount = 0;
  const sampleCodes = [];

  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    const isPrintable = code >= 0x20 && code <= 0x7e;
    if (!isPrintable) {
      nonPrintableCount += 1;
      if (sampleCodes.length < 8) {
        sampleCodes.push(`@${i}:0x${code.toString(16)}`);
      }
    }
  }

  const printableRatio = `${Math.round(((value.length - nonPrintableCount) / value.length) * 100)}%`;

  return {
    printableRatio,
    nonPrintableCount,
    hasWhitespace: /\s/.test(value),
    sampleCodes: sampleCodes.join(', ') || '(none)'
  };
};

/**
 * Creates a DirectLine object with custom headers
 * @param {string} directLineToken - The DirectLine token for authentication
 * @param {function} getSecureToken - Function to get the JWT token
 * @param {object} store - The Vue store instance
 * @returns {object} DirectLine object with custom headers
 */
export const createDirectLineWithHeaders = (directLineToken, getSecureToken, store) => {
  try {
    const headerSafe = isValidHttpHeaderValue(directLineToken);
    const charsetInfo = inspectTokenCharset(directLineToken);

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[ChatbotUtils]: createDirectLineWithHeaders debug', {
        directLineTokenMasked: maskSensitiveValue(directLineToken),
        isValidHttpHeaderValue: headerSafe,
        ...charsetInfo
      });
    }
    if (!headerSafe) {
      throw new Error(
        'Direct Line token contains invalid header characters (likely decrypt key/userId mismatch). Check console decrypt debug logs.'
      );
    }

    // Create the standard DirectLine object
    const directLine = window.WebChat.createDirectLine({
      token: directLineToken,
      webSocket: true // Use WebSocket if possible for better performance
    });
    
    // Create a wrapper with the same interface as DirectLine
    return {
      activity$: directLine.activity$,
      connectionStatus$: directLine.connectionStatus$,
      
      // Override the postActivity method to add custom headers
      postActivity: activity => {
        try {
          // Add your custom headers to every activity
          const activityWithHeaders = {
            ...activity,
            channelData: {
              ...activity.channelData,
              headers: {
                'x-jwt-token': getSecureToken() || '',
              },
              params: {
                ...(store.state.client && { 'clientId': store.state.client }),
                ...(store.state.chatbot.directLineTenant && { 'tenantId': store.state.chatbot.directLineTenant }),
                ...(store.state.chatbot.project && { 'projectId': store.state.chatbot.project }),
                ...(store.state.chatbot.task && { 'taskId': store.state.chatbot.task }),
                ...(store.state.chatbot.filters && { 'filters': store.state.chatbot.filters }),
                ...(store.state.chatbot.role && { 'role': store.state.chatbot.role }),
                ...(store.state.user?.name && { 'name': store.state.user.name }),
              }
            }
          };
          
          // Pass the modified activity to the original postActivity method
          return directLine.postActivity(activityWithHeaders)
            .catch(error => {
              console.error('Error posting activity:', error);
              throw error;
            });
        } catch (error) {
          console.error('Error in postActivity:', error);
          throw error;
        }
      }
    };
  } catch (error) {
    console.error('Error creating DirectLine with headers:', error);
    // Return a dummy DirectLine object that does nothing
    return {
      activity$: { subscribe: () => ({ unsubscribe: () => {} }) },
      connectionStatus$: { subscribe: () => ({ unsubscribe: () => {} }) },
      postActivity: () => Promise.reject(new Error('Chat not initialized'))
    };
  }
};

/**
 * Resolve chatbot projectId / taskId from the current Vue route.
 * Checklist: `/financialclose/project/checklist/:id` → projectId only
 * Task detail: `/financialclose/project/checklist/:projectId/task/:taskId` → both
 * @param {object} route - Vue Router route
 * @returns {{ projectId: string|null, taskId: string|null }|null}
 *   null when the route is outside Financial Close (leave store unchanged)
 */
export const extractChatbotContextFromRoute = (route = {}) => {
  const path = route?.path || '';
  if (!path.includes('/financialclose/')) {
    return null;
  }

  // Setup masters use :id for template ids — not a cycle/project id.
  if (path.includes('/financialclose/setup/')) {
    return { projectId: null, taskId: null };
  }

  const params = route?.params || {};

  // Task detail page
  if (params.projectId && params.taskId) {
    return {
      projectId: params.projectId,
      taskId: params.taskId,
    };
  }

  // Cycles / checklist and other FC cycle pages (`:id` is the cycle)
  const projectId = params.id || params.projectId || null;
  return {
    projectId,
    taskId: null,
  };
};

/**
 * Load the WebChat script dynamically
 * @param {function} callback - Function to call when script is loaded
 * @returns {HTMLScriptElement} The created script element
 */
export const loadWebChatScript = (callback) => {
  const script = document.createElement('script');
  script.src = 'https://cdn.botframework.com/botframework-webchat/latest/webchat.js';
  script.async = true;
  script.onload = callback;
  document.head.appendChild(script);
  return script;
};
